import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Tooltip } from 'primereact/tooltip';
import { DocDetails } from '../../../../../api/types/docs/doc-details';
import { DocFeedback } from '../../../../../api/types/docs/doc-feedback';
import { DocFeedbackStatusDB } from '../../../../../api/types/docs/doc-feedback-status';
import { AddFeedbackDialog } from './add-feedback-dialog';

interface DocumentFeedbackDialogProps {
  visible: boolean;
  onHide: () => void;
  document: DocDetails | null;
  userId: number;
  onFeedbackAdded: () => void;
}

export const DocumentFeedbackDialog: React.FC<DocumentFeedbackDialogProps> = ({
  visible,
  onHide,
  document,
  userId,
  onFeedbackAdded,
}) => {
  const [showAddFeedbackDialog, setShowAddFeedbackDialog] = useState(false);

  if (!document) return null;

  const feedbacks = document.feedbacks || [];
  const lastFeedback =
    feedbacks.length > 0 ? feedbacks[feedbacks.length - 1] : null;
  const isLastFeedbackApproved =
    lastFeedback?.status === DocFeedbackStatusDB.Approved;

  const canAddFeedback = !isLastFeedbackApproved;

  const handleAddFeedbackSuccess = async () => {
    setShowAddFeedbackDialog(false);
    await onFeedbackAdded();
  };

  // Template functions for DataTable columns
  const statusTemplate = (feedback: DocFeedback) => {
    switch (feedback.status) {
      case DocFeedbackStatusDB.Approved:
        return <Tag value="אושר" severity="success" />;
      case DocFeedbackStatusDB.Reject:
        return <Tag value="נדחה" severity="danger" />;
      case DocFeedbackStatusDB.Init:
        return <Tag value="בבדיקה" severity="warning" />;
      default:
        return <Tag value="לא ידוע" severity="secondary" />;
    }
  };

  const contentTemplate = (feedback: DocFeedback) => {
    return feedback.content || '-';
  };

  const createdAtTemplate = (feedback: DocFeedback) => {
    return new Date(feedback.createdAt).toLocaleDateString('he-IL');
  };

  const createdByTemplate = (feedback: DocFeedback) => {
    return feedback.createdByName || '-';
  };

  const fileTemplate = (feedback: DocFeedback) => {
    if (!feedback.fileUrl) {
      return '-';
    }
    return (
      <Button
        label="הורד קובץ"
        icon="pi pi-download"
        size="small"
        text
        onClick={() => window.open(feedback.fileUrl, '_blank')}
      />
    );
  };

  return (
    <>
      <Dialog
        header={`משובים עבור: ${document.docMetadata.docName}`}
        visible={visible}
        onHide={onHide}
        style={{ width: '90vw', maxWidth: '1200px' }}
        footer={
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div>
              {!canAddFeedback && (
                <>
                  <Tooltip target=".add-feedback-disabled-btn" />
                  <span
                    className="add-feedback-disabled-btn"
                    data-pr-tooltip="לא ניתן להוסיף משוב - המסמך אושר"
                    data-pr-position="top"
                  >
                    <Button
                      label="הוסף משוב"
                      icon="pi pi-plus"
                      disabled={true}
                      style={{ pointerEvents: 'none' }}
                    />
                  </span>
                </>
              )}
              {canAddFeedback && (
                <Button
                  label="הוסף משוב"
                  icon="pi pi-plus"
                  onClick={() => setShowAddFeedbackDialog(true)}
                />
              )}
            </div>
            <Button
              label="סגור"
              icon="pi pi-times"
              onClick={onHide}
              className="p-button-text"
            />
          </div>
        }
      >
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ marginBottom: '0.5rem' }}>פרטי המסמך</h3>
          <p>
            <strong>תיאור:</strong> {document.docMetadata.docDescription || '-'}
          </p>
          <p>
            <strong>תאריך יצירה:</strong>{' '}
            {new Date(document.docMetadata.createdAt).toLocaleDateString(
              'he-IL',
            )}
          </p>
          <p>
            <strong>נוצר על ידי:</strong>{' '}
            {document.docMetadata.createdByName || '-'}
          </p>
        </div>

        <h3 style={{ marginBottom: '1rem' }}>רשימת משובים</h3>
        {feedbacks.length === 0 ? (
          <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
            <p>אין משובים עבור מסמך זה</p>
          </div>
        ) : (
          <DataTable
            value={feedbacks}
            stripedRows
            showGridlines
            style={{ direction: 'rtl' }}
          >
            <Column
              header="תוכן המשוב"
              body={contentTemplate}
              style={{ textAlign: 'right', maxWidth: '300px' }}
            />
            <Column
              header="סטטוס"
              body={statusTemplate}
              style={{ textAlign: 'right', width: '120px' }}
            />
            <Column
              header="תאריך יצירה"
              body={createdAtTemplate}
              style={{ textAlign: 'right', width: '150px' }}
            />
            <Column
              header="נוצר על ידי"
              body={createdByTemplate}
              style={{ textAlign: 'right', width: '150px' }}
            />
            <Column
              header="קובץ"
              body={fileTemplate}
              style={{ textAlign: 'right', width: '150px' }}
            />
          </DataTable>
        )}
      </Dialog>

      <AddFeedbackDialog
        visible={showAddFeedbackDialog}
        onHide={() => setShowAddFeedbackDialog(false)}
        userId={userId}
        docId={document.docMetadata.id}
        onFeedbackAdded={handleAddFeedbackSuccess}
      />
    </>
  );
};
